import { Component } from '@angular/core';
import { OnInit, Input, Output, EventEmitter, ViewChild, ElementRef, OnChanges, SimpleChanges } from '@angular/core';
import { SafeResourceUrl } from '@angular/platform-browser';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ConfigService } from '../../services/config.service';
import { UserService } from '../../services/user.service';
import { ViewerService } from '../../services/viewer.service';
import { ViewportService } from '../../services/viewport.service';
import { SearchService } from '../../search/services/search.service';
import { AnnotationEditorComponent } from '../../workflow/components/annotation-editor/annotation-editor.component';

import { ViewerAnnotation } from '../../models/viewer.interface';

import { formatString } from '../../utils/string-utils';

/**
 * Provides a view of a pdf product.
 */
@Component({
	selector: 'doc-pdf-view',
	templateUrl: './doc-pdf-view.component.html',
	styleUrl: './doc-pdf-view.component.css'
})
export class DocPdfViewComponent implements OnInit {

	@Input() product!: any;
	@Input() documentData!: { [key: string]: any };
	@Input() viewerURL: SafeResourceUrl | undefined;
	@Input() package!: any;
	@Input() page!: any;
	@Input() selectedPage!: number;

	@Output() triggerCurrentViewModeChange = new EventEmitter<{ mode: string }>();
	@Output() triggerCurrentPageChange = new EventEmitter<{ pageNumber: number }>();

	@ViewChild('pdfIframe') pdfIframeRef!: ElementRef<HTMLIFrameElement>;

	LOCALIZATION: any;
	userPermissions: { [key: string]: any } = {};
	dateFormatDisplay: string = 'MM/DD/YY';

	isMobile: boolean = false;

	pdf: {
		currentPage: number;
		viewMode: string;
		isSinglePageMode: () => boolean;
		highlightedPages: number;
		currentHighlightedPage: number;
		currentHighlight: any;
		currentHighlightIdx: number | undefined;
		viewerAnnotations: ViewerAnnotation[];
	} = {
			currentPage: 0,
			viewMode: 'Full',
			isSinglePageMode: () => { return this.pdf.viewMode === 'Single'; },
			highlightedPages: 0,
			currentHighlightedPage: 0,
			currentHighlight: undefined,
			currentHighlightIdx: undefined,
			viewerAnnotations: []
		};
	pdfpagenumber: number = 0; // Current pdf page number	

	constructor(
		private ngbModal: NgbModal,
		private userService: UserService,
		private configService: ConfigService,
		private searchService: SearchService,
		private viewportService: ViewportService,
		private viewerService: ViewerService,
	) {
		this.LOCALIZATION = this.userService.LOCALIZATION;
		this.userPermissions = this.userService.getUserPermissionsMap();
	}

	ngOnInit(): void {
		// Replacement for the viewportsizemonitor directive
		this.viewportService.isMobile$.subscribe(isMobile => {
			this.isMobile = isMobile;
		});

		var property = this.userService.user.properties.awpropertyObjectList.find((x: any) => { return x.name == "PDF_DOCUMENT_DISPLAY_STYLE" });
		if (property) {
			this.pdf.viewMode = property.value === "Full View" ? "Full" : "Single";
		}

		this.loadDocument();
	}
	
	/**
	 * Reinitialize this component without destroying the DOM.
	 */
	ngOnChanges(changes: SimpleChanges) {
		if (changes['viewerURL'] || changes['product'] || changes['selectedPage']) {
			this.loadDocument();
		}
	}

	private loadDocument(): void {
		this.resetPdfPager();

		if (this.pdf.isSinglePageMode()) {
			this.togglePdfPageMode("Single");
		} else {
			this.loadFullPdf(this.page);
		}

		this.triggerCurrentViewModeChange.emit({ mode: this.pdf.viewMode });
	}

	private resetPdfPager(): void {
		if (!this.pdf) return;
		if (this.pdf.isSinglePageMode()) {
			this.pdf.currentPage = 1;
			let firstHit = parseInt(this.documentData['searchHitData'].hitIndex[0]);
			if (!isNaN(firstHit)) {
				this.pdf.currentPage = parseInt(this.documentData['searchHitData'].hitIndex[0]);
			}
			this.pdf.highlightedPages = this.documentData['searchHitData']?.hitIndex
				? this.documentData['searchHitData'].hitIndex.length
				: 0;
			this.pdf.currentHighlightedPage = this.getCurrentHighlightedPageIndex();
			this.documentData['searchHitData'].closestHitIndex =
				this.viewerService.findClosestHit(this.pdf.currentPage, this.documentData['searchHitData'].hitIndex, this.pdf.viewMode);
		}
	}

	private getCurrentHighlightedPageIndex(): number {
		let hitIndex = this.documentData['searchHitData']?.hitIndex
			? this.documentData['searchHitData'].hitIndex
			: [];
		let pageNumber = this.pdf.currentPage + "";
		let idx = hitIndex.indexOf(pageNumber) + 1;
		return idx;
	}

	togglePdfPageMode(mode: string): void {
		this.pdf.viewMode = mode;
		this.triggerCurrentViewModeChange.emit({ mode: this.pdf.viewMode });

		if (this.pdf.isSinglePageMode()) {
			this.pdf.currentPage = this.pdf.currentPage ? this.pdf.currentPage : 1;
			this.pdf.highlightedPages = (this.documentData['searchHitData'] && this.documentData['searchHitData'].hitIndex)
				? this.documentData['searchHitData'].hitIndex.length
				: 0;

			// On initial load, jump to the first highlighted pdf page
			let hitData = this.documentData['searchHitData'];
			let highlightIdx = this.pdf.currentHighlightIdx;
			if (hitData && hitData.hasHit) {
				this.pdf.currentPage = highlightIdx ? this.pdf.viewerAnnotations[highlightIdx].pdfPage : this.documentData['searchHitData'].pageNumber;
			}

			this.pdf.currentHighlightedPage = this.getCurrentHighlightedPageIndex();
			if (this.documentData['searchHitData']) {
				this.documentData['searchHitData'].closestHitIndex = this.viewerService.findClosestHit(this.pdf.currentPage, this.documentData['searchHitData'].hitIndex, this.pdf.viewMode);
			}

			this.getCurrentPdfPage();
		}
		else {
			this.loadFullPdf(this.package.pages[this.selectedPage]);
		}
	}

	prevPdfPage(): void {
		if ((this.pdf.currentPage - 1) == 0) { return; }
		--this.pdf.currentPage;
		this.moveToPage();
	}

	nextPdfPage(): void {
		let pdfpages = this.package.pages[this.selectedPage]?.productPageMetadata?.pages || this.documentData['pages'];
		if ((this.pdf.currentPage + 1) > pdfpages) { return; }
		this.pdf.currentPage++;
		this.moveToPage();
	}

	private moveToPage(): void {
		if (this.documentData['searchHitData'] && this.documentData['searchHitData'].hitIndex) {
			this.documentData['searchHitData'] = this.viewerService.buildSearchHitData(this.pdf.currentPage, this.documentData['searchHitData'].hitIndex, this.pdf.viewMode);
		}

		this.pdf.currentHighlightedPage = this.getCurrentHighlightedPageIndex();
		this.getCurrentPdfPage();

		this.triggerCurrentPageChange.emit({ pageNumber: this.pdf.currentPage });
	}

	private getCurrentPdfPage(): void {
		let documentURL = null;

		if (this.searchService.query) {
			let searchString = this.viewerService.buildDocIdSearchString();

			documentURL = this.package.pages.length === 0
				? formatString(this.configService.getConfig().document.documentProviderPageHighlightURL, this.product.id, (this.pdf.currentPage || 1), searchString, 0)
				: formatString(this.configService.getConfig().document.documentProviderPageHighlightPackageURL, this.product.id, (this.selectedPage + 1), searchString, this.pdf.currentPage || 0)
		} else {
			documentURL = this.package.pages.length === 0
				? formatString(this.configService.getConfig().document.documentProviderPageURL, this.product.id, this.pdf.currentPage, 0)
				: formatString(this.configService.getConfig().document.documentProviderPackagePageURL, this.product.id, this.selectedPage + 1, this.pdf.currentPage || 0)
		}
		documentURL += `#zoom=${this.viewerService.getZoom()}`;

		this.viewerURL = this.viewerService.generateViewerURL(`app/js/vendor/pdfjs/web/viewer.html?file=${encodeURIComponent(documentURL)}`);

		//		            if (gtag) {
		//		                if (this.pdf.viewMode === 'Single') {
		//		                    let record = this.searchResults.records[this.searchResults.current - 1];
		//		                    let collectionField = userService.user.collectionField;
		//		                    if (record) {
		//		                        let collection = record[collectionField];
		//		                        if (Array.isArray(collection)) {
		//		                            for (let i = 0; i < collection.length; i++) {
		//		                                let c = collection[i];
		//		                                let event_description = {
		//		                                    'event_category': 'engagement',
		//		                                    'event_label': 'Viewed page in collection',
		//		                                    'collection': c,
		//		                                    'id': this.product.id,
		//		                                    'title': this.title,
		//		                                    'page': this.pdf.currentPage,
		//		                                    'username': userService.user.username,
		//		                                    'userID': userService.user.id
		//		                                };
		//										// TODO: use analyticsService.trackEvent
		//		                                userService.sendgtag('event', 'document_page_viewed', event_description);
		//		                            };
		//		                        }
		//		                        else {
		//		                            collection = collection ? collection : "Empty";
		//		                            let event_description = {
		//		                                'event_category': 'engagement',
		//		                                'event_label': 'Viewed page in collection',
		//		                                'collection': collection,
		//		                                'id': this.product.id,
		//		                                'title': this.title,
		//		                                'page': this.pdf.currentPage,
		//		                                'username': userService.user.username,
		//		                                'userID': userService.user.id
		//		                            };
		//									// TODO: use analyticsService.trackEvent
		//		                            userService.sendgtag('event', 'document_page_viewed', event_description);
		//		                        }
		//		                    }
		//		                }
		//		            }
	}

	private loadFullPdf(subpage?: any): void {
		// If subpage exists then it means we are dealing with an embedded PDF in a multipage package type.
		if (this.documentData['documentType'] === "application/pdf" || subpage?.fileType === "application/pdf") {
			// Jump to the first highlighted pdf page
			let hitData = this.documentData['searchHitData'];

			console.log(`hitData:`);
			console.log(hitData);

			// documentData.searchHitData.pageNumber = 1 based
			// documentData.pages  = 0 based
			let page = 1;
			let pdfpages = this.documentData['pages'] + 1;
			if (hitData && hitData.hasHit && pdfpages >= 1) {
				page = hitData.pageNumber;
			}

			if (this.pdf) {
				let viewerAnnotations = this.pdf.viewerAnnotations;
				let currentIdx = this.pdf.currentHighlightIdx;
				if (viewerAnnotations && currentIdx) {
					this.pdf.currentHighlight = this.pdf.viewerAnnotations[currentIdx];
					page = this.pdf.currentPage;
				}
				this.pdf.currentPage = page;
			}

			// The URL is decoded by pdfjs prior to fetching the document
			// Wildfly 21 is stricter about characters in the URL
			let searchString = this.viewerService.buildDocIdSearchString();
			let documentURL = (subpage && (subpage.pageNumber || subpage.pageNum))
				? formatString(this.configService.getConfig().document.documentProviderPageHighlightURL, this.product.id, subpage.pageNumber || subpage.pageNum, searchString)
				: formatString(this.configService.getConfig().document.documentProviderHighlightURL, this.product.id, searchString);
			documentURL += '#page=' + page;

			this.viewerURL = this.viewerService.generateViewerURL(`app/js/vendor/pdfjs/web/viewer.html?file=${encodeURIComponent(documentURL)}`);
		}

		//		if (gtag) {
		//                if (this.pagesGA && this.pagesGA.length > 0) {
		//			          let record = this.searchResults.records[this.searchResults.current - 1];
		//                    let collectionField = userService.user.collectionField;
		//                    if (record) {
		//                        let collection = record[collectionField];
		//                        if (Array.isArray(collection)) {
		//                            for (let i = 0; i < collection.length; i++) {
		//                                let c = collection[i];
		//                                let event_description = {
		//                                    'event_category': 'engagement',
		//                                    'event_label': 'Viewed page in collection',
		//                                    'id': this.product.id,
		//                                    'collection': c,
		//                                    'title': this.title,
		//                                    'total_pages_viewed': this.pagesGA.length,
		//                                    'username': userService.user.username,
		//                                    'userID': userService.user.id,
		//                                    'domain': userService.user.domain
		//                                };
		//								// TODO: use analyticsService.trackEvent
		//                                userService.sendgtag('event', 'total_document_pages_viewed', event_description);
		//                            }
		//                        }
		//                        else {
		//                            collection = collection ? collection : "Empty";
		//                            let event_description = {
		//                                'event_category': 'engagement',
		//                                'event_label': 'Viewed page in collection',
		//                                'id': this.product.id,
		//                                'collection': collection,
		//                                'title': this.title,
		//                                'total_pages_viewed': this.pagesGA.length,
		//                                'username': userService.user.username,
		//                                'userID': userService.user.id,
		//                                'domain': userService.user.domain
		//                            };
		//							// TODO: use analyticsService.trackEvent
		//                            userService.sendgtag('event', 'total_document_pages_viewed', event_description);
		//                        }
		//                    }
		//                }
		//		            }
		//		            this.pagesGA = [];
	}

	pdf_viewer_onload(event: Event): void {
		console.log("viewer iframe loaded - event triggered");

		this.scrollToPdfHighlight();
	}

	private showPdfHighlight(selected: any): void {
		let pageIndex = selected.pdfPage - 1;
		if (this.pdf.isSinglePageMode()) {
			// single view is always page 1
			pageIndex = 0;
		}

		try {
			const iframe = this.pdfIframeRef.nativeElement;
			const viewerApp = (iframe.contentWindow as any)?.PDFViewerApplication;
			if (!viewerApp) return;

			let page = viewerApp.pdfViewer.getPageView(pageIndex);

			if (!page?.canvas) {
				// Ensure the page is rendered, then scroll
				viewerApp.page = selected.pdfPage;

				setTimeout(() => {
					page = viewerApp.pdfViewer.getPageView(pageIndex);
					this.scrollSelectedIntoView(page, selected);
				}, 250);
			} else {
				this.scrollSelectedIntoView(page, selected);
			}
		} catch (err) {
			console.error('Could not access PDFViewerApplication:', err);
		}
	}

	private scrollSelectedIntoView(page: any, selected: any): void {
		const pageElement = page.canvas?.parentElement;
		const viewport = page.viewport;

		if (!pageElement || !viewport) return;

		const coords = selected.area?.boundingBox || [];
		const rect = [coords[0], coords[1], coords[4], coords[5]];
		const bounds = viewport.convertToViewportRectangle(rect);

		// Remove existing highlights
		pageElement.querySelectorAll('.kvannot').forEach((el: any) => el.remove());

		// Create highlight overlay
		const el = document.createElement('div');
		el.setAttribute(
			'style',
			`
		    position: absolute;
		    background-color: #f7f90126;
		    left: ${Math.min(bounds[0], bounds[2])}px;
		    top: ${Math.min(bounds[1], bounds[3])}px;
		    width: ${Math.abs(bounds[0] - bounds[2])}px;
		    height: ${Math.abs(bounds[1] - bounds[3])}px;
		  `
		);
		el.className = 'kvannot';
		pageElement.appendChild(el);

		el.scrollIntoView({
			behavior: 'smooth',
			block: 'start',
			inline: 'nearest',
		});
	}

	private scrollToPdfHighlight(): void {
		this.pdfpagenumber = this.pdf.currentPage - 1;

		// Wait until the pdf is loaded before scrolling
		this.initializePDFEvents();
	}

	private initializePDFEvents(): void {
		if (!this.pdfIframeRef) {
			console.error('iframe not ready yet!');
			return;
		}

		const iframe = this.pdfIframeRef.nativeElement;

		const viewerApp = (iframe.contentWindow as any)?.PDFViewerApplication;
		if (!viewerApp) {
			console.warn('PDFViewerApplication not found on iframe window.');
			return;
		}
		// NOTE!
		// As of pdfJS v2.2.228, requires eventBusDispatchToDOM set to TRUE in viewer.js in order for events to reach the DOM
		// As of March 2020, the eventBusDispatchToDOM preference is deprecated, add event listeners to the EventBus instance rather than the DOM,
		// eg.
		// PDFViewerApplication.eventBus.on("documentloaded", function(event) {
		//    console.log("The document has now been loaded");
		// });		
		viewerApp.initializedPromise.then(() => {
			viewerApp.eventBus.on('documentloaded', this.ondocumentrendered.bind(this), { once: true });
			viewerApp.eventBus.on('pagechanging', this.onpagechanged.bind(this), { once: false });
		});
	}

	private ondocumentrendered(): void {
		console.log('PDF document loaded');

		this.transformDocumentLinks();

		// Fetch the highlight annotations and scroll to the first hit
		this.pdf.viewerAnnotations = [];

		let searchString = this.viewerService.buildHighlightSearchString();

		this.viewerService.getPdfAnnotationModel(this.product.id, searchString).then(
			(response: any) => {
				this.pdf.viewerAnnotations = response;
				let page = Number(this.pdf.currentPage);

				// Get the first annotation on the page
				const firstannot = this.pdf.currentHighlightIdx != null
					? this.pdf.viewerAnnotations[this.pdf.currentHighlightIdx]
					: this.pdf.viewerAnnotations.find((a: any) => a.pdfPage === page);

				if (!firstannot) { return; }
				if (firstannot.pdfPage != page) { return; }

				this.pdf.currentHighlight = firstannot;

				this.showPdfHighlight(this.pdf.currentHighlight);
			},
			(err: any) => {
				console.error("Failed to fetch annotation model:", err);
				this.pdf.viewerAnnotations = [];
			}
		);
	}

	private onpagechanged(event: any): void {
		console.log('Page changed:', event.pageNumber);
		this.pdfpagenumber = event.pageNumber - 1;
	}

	// TODO: Why do we need this?
	private transformDocumentLinks(): void {
		// Loop through the anchors and set target="_blank", do not change links for video transcription HTML

		try {
			const iframe = this.pdfIframeRef.nativeElement;
			const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
			if (!iframeDoc) return;

			const anchors = iframeDoc.getElementsByTagName('a');
			for (let i = 0; i < anchors.length; i++) {
				const anchor = anchors[i];

				const hasStartFrom = anchor.onclick?.toString().includes('startFrom(');
				if (!hasStartFrom) {
					let href = anchor.getAttribute('href') || '';

					if ((href.includes('.htm') || href.includes('.pdf')) && this.product.id) {
						href = `${href}/${this.product.id}`;
					}

					anchor.setAttribute('href', href);
					anchor.setAttribute('target', '_blank');
				}
			}

			// Update all images
			const images = iframeDoc.getElementsByTagName('img');
			for (let i = 0; i < images.length; i++) {
				const img = images[i];
				let src = img.getAttribute('src') || '';

				if (src.includes('/aw-server') && this.product.id) {
					src = `${src}/${this.product.id}`;
					img.setAttribute('src', src);
				}
			}
		} catch (err) {
			console.warn('Could not access iframe content:', err);
		}
	}

	prevPdfHit(): void {
		if (!this.hasPrevPdfHit() || this.pdf.currentHighlightIdx === undefined) return;
		this.pdf.currentHighlight = this.pdf.viewerAnnotations[--this.pdf.currentHighlightIdx];
		this.moveToPdfHit();
	}

	nextPdfHit(): void {
		if (!this.hasNextPdfHit() || this.pdf.currentHighlightIdx === undefined) return;
		this.pdf.currentHighlight = this.pdf.viewerAnnotations[++this.pdf.currentHighlightIdx];
		this.moveToPdfHit();
	}

	private moveToPdfHit(): void {
		this.showPdfHighlight(this.pdf.currentHighlight);
		if (this.pdf.isSinglePageMode() && this.pdf.currentPage !== this.pdf.currentHighlight.pdfPage) {
			this.pdf.currentPage = this.pdf.currentHighlight.pdfPage;
			this.getCurrentPdfPage();
		}
	}

	hasPrevPdfHit(): boolean {
		let idx = undefined;
		for (let i = 0, len = this.pdf.viewerAnnotations.length; i < len; i++) {
			let annot = this.pdf.viewerAnnotations[i];
			if (this.pdf.currentHighlight &&
				annot.pdfPage == this.pdf.currentHighlight.pdfPage &&
				annot.area.vertices[0][0] == this.pdf.currentHighlight.area.vertices[0][0] &&
				annot.area.vertices[0][1] == this.pdf.currentHighlight.area.vertices[0][1]) {
				idx = i;
				break;
			}
		}
		this.pdf.currentHighlightIdx = idx;
		if (idx == undefined || idx - 1 == -1) {
			return false;
		}
		return true;
	}

	hasNextPdfHit(): boolean {
		let idx = undefined;
		for (let i = 0, len = this.pdf.viewerAnnotations.length; i < len; i++) {
			let annot = this.pdf.viewerAnnotations[i];
			if (this.pdf.currentHighlight &&
				annot.pdfPage == this.pdf.currentHighlight.pdfPage &&
				annot.area.vertices[0][0] == this.pdf.currentHighlight.area.vertices[0][0] &&
				annot.area.vertices[0][1] == this.pdf.currentHighlight.area.vertices[0][1]) {
				idx = i;
				break;
			}
		}
		this.pdf.currentHighlightIdx = idx;
		if (idx == undefined || idx + 1 >= this.pdf.viewerAnnotations.length) {
			return false;
		}
		return true;
	}


	/**
	 * Displays the product in the annotation editor.
	 */
	openAnnotationEditor(): void {
		const modalRef = this.ngbModal.open(AnnotationEditorComponent, {
			windowClass: 'dx-modal dx-viewer dx-viewer-no-task'
		});

		let searchString = this.viewerService.buildHighlightSearchString();

		modalRef.componentInstance.editorData = {
			productTitle: this.product.title,
			productID: this.product.id,
			searchString: searchString
		};

		modalRef.result.then(
			(payload) => {
				console.log('Modal dismissed');
			},
			(reason) => {
				console.log('Modal dismissed');
			}
		);
	}
}
